/**
 * AllocationStepper.tsx
 * Editable allocation-% control for one person on a WO/quote-week roster
 * (PR B — D3). Restored + adapted from the Wave-3-retired WorkloadBar
 * (git 391280c1): a small filled bar + numeric %, with −/+ steppers.
 *
 * The bar fills toward 100%; the fill turns red at over-allocation (>100%) so
 * an over-booked person reads at a glance (D4 — advisory only, never blocks).
 * Thin + presentational: the parent owns the debounce + the PUT.
 *
 * Two presentations (`variant`):
 *   - 'bar'    (default) — a small filled progress bar + trailing % (legacy).
 *   - 'number' — a prominent centered % flanked by the −/+ steppers, NO bar
 *                (STAFF-VIEW-V2 cell redesign: the ring already encodes total
 *                load, so this-plan % reads as a plain number, not a second bar).
 * Both share the identical clamp / step / onChange edit logic.
 *
 * Colors come from theme.schedule.* tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useTheme, alpha } from '@mui/material/styles';

export interface AllocationStepperProps {
  /** Current allocation percent (0–500; clamped for display). */
  percent: number;
  /** Called with the new value when the user steps up/down. */
  onChange: (newPercent: number) => void;
  /** Step size (default 25 — the common quarter-time increments). */
  step?: number;
  /** Disable interaction (plan locked / saving). */
  disabled?: boolean;
  /** Presentation: 'bar' (filled progress bar) | 'number' (big % + steppers, no bar). */
  variant?: 'bar' | 'number';
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export const AllocationStepper: React.FC<AllocationStepperProps> = ({
  percent,
  onChange,
  step = 25,
  disabled = false,
  variant = 'bar',
}) => {
  const theme = useTheme();
  const value = clamp(Math.round(percent), 0, 500);
  const over = value > 100;

  // Fill is proportional to 100% capacity; over-allocation pins the bar full + red.
  const fillPct = Math.min(100, value);
  const barColor = over ? theme.schedule.allocOver : theme.schedule.employeeAlloc;
  const trackColor = alpha(barColor, 0.18);

  const stepDown = useCallback(() => {
    if (disabled) return;
    onChange(clamp(value - step, 0, 500));
  }, [disabled, onChange, value, step]);

  const stepUp = useCallback(() => {
    if (disabled) return;
    onChange(clamp(value + step, 0, 500));
  }, [disabled, onChange, value, step]);

  // 'number' — big centered % flanked by the steppers, no bar. The ring above
  // already encodes total load, so this-plan % reads as a plain number.
  if (variant === 'number') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, userSelect: 'none' }}>
        <Tooltip title={`Reduce by ${step}%`} arrow>
          <span>
            <IconButton
              size="small"
              onClick={stepDown}
              disabled={disabled || value <= 0}
              aria-label="Reduce allocation"
              sx={{ p: 0.25 }}
            >
              <RemoveIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          sx={{
            fontSize: '1.05rem',
            fontWeight: 800,
            lineHeight: 1,
            color: barColor,
            minWidth: 46,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {value}%
        </Typography>

        <Tooltip title={`Increase by ${step}%`} arrow>
          <span>
            <IconButton
              size="small"
              onClick={stepUp}
              disabled={disabled}
              aria-label="Increase allocation"
              sx={{ p: 0.25 }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, width: '100%', minWidth: 120, userSelect: 'none' }}>
      <Tooltip title={`Reduce by ${step}%`} arrow>
        <span>
          <IconButton
            size="small"
            onClick={stepDown}
            disabled={disabled || value <= 0}
            aria-label="Reduce allocation"
            sx={{ p: 0.25 }}
          >
            <RemoveIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Bar track + fill */}
      <Box
        sx={{
          flex: 1,
          height: 10,
          borderRadius: 5,
          backgroundColor: trackColor,
          position: 'relative',
          overflow: 'hidden',
          minWidth: 44,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${fillPct}%`,
            backgroundColor: barColor,
            borderRadius: 5,
            transition: 'width 0.15s ease, background-color 0.15s ease',
          }}
        />
      </Box>

      <Tooltip title={`Increase by ${step}%`} arrow>
        <span>
          <IconButton
            size="small"
            onClick={stepUp}
            disabled={disabled}
            aria-label="Increase allocation"
            sx={{ p: 0.25 }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Numeric label */}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: barColor,
          minWidth: 34,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {value}%
      </Typography>
    </Box>
  );
};
